import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Home, Briefcase, Clock, CheckCircle, AlertCircle,
  Building, ChevronRight, Loader2, FileText, Users,
  TrendingUp, Calendar, Truck
} from "lucide-react";
import { API } from "@/App";
import { RemovalsQuoteModal } from "@/components/RemovalsQuoteModal";

const stageLabels = {
  offer_accepted: "Offer Accepted",
  memo_issued: "Memo Issued",
  aml_verified: "AML Verified",
  proof_of_funds: "Proof of Funds",
  mortgage_offer: "Mortgage Offer",
  contract_pack: "Contract Pack",
  searches_ordered: "Searches Ordered",
  searches_returned: "Searches Returned",
  enquiries_raised: "Enquiries Raised",
  enquiries_resolved: "Enquiries Resolved",
  exchange_ready: "Exchange Ready",
  exchanged: "Exchanged",
  completion: "Completed"
};

const stageOrder = [
  "offer_accepted", "memo_issued", "aml_verified", "proof_of_funds",
  "mortgage_offer", "contract_pack", "searches_ordered", "searches_returned",
  "enquiries_raised", "enquiries_resolved", "exchange_ready", "exchanged", "completion"
];

// Milestones that solicitors are responsible for
const SOLICITOR_MILESTONES = {
  buyer_solicitor: ["searches_ordered", "searches_returned", "enquiries_raised"],
  seller_solicitor: ["contract_pack", "enquiries_resolved"]
};

export default function SolicitorDashboard() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  
  const [loading, setLoading] = useState(true);
  const [cases, setCases] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    exchanged: 0,
    completed: 0,
    needsAction: 0
  });

  // Removal Quote Modal State
  const [quoteModal, setQuoteModal] = useState(false);

  useEffect(() => {
    fetchMyCases();
  }, []);

  const fetchMyCases = async () => {
    try {
      const response = await axios.get(`${API}/sales/solicitor/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const casesData = response.data.cases || [];
      setCases(casesData);
      
      // Calculate stats
      const active = casesData.filter(c => c.status === "active").length;
      const exchanged = casesData.filter(c => c.status === "exchanged").length;
      const completed = casesData.filter(c => c.status === "completed").length;
      const needsAction = casesData.filter(c => c.pending_milestones?.length > 0).length;
      
      setStats({
        total: casesData.length,
        active,
        exchanged,
        completed,
        needsAction
      });
    } catch (error) {
      console.error("Error fetching cases:", error);
      toast.error("Failed to load your cases");
    } finally {
      setLoading(false);
    }
  };

  const getStageProgress = (currentStage) => {
    const idx = stageOrder.indexOf(currentStage);
    return idx >= 0 ? Math.round(((idx + 1) / stageOrder.length) * 100) : 0;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short"
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-600 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-semibold">My Cases with Goodacres</h1>
                <p className="text-sm text-muted-foreground">Welcome back, {user.name}</p>
              </div>
            </div>
            <Button variant="outline" onClick={() => {
              localStorage.removeItem("token");
              localStorage.removeItem("user");
              navigate("/login");
            }}>
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* H&E Removals Banner - TOP */}
        <Card className="mb-6 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white border-0 overflow-hidden">
          <CardContent className="p-5">
            <div className="flex items-center justify-between gap-6 flex-wrap">
              <div className="flex items-center gap-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_estate-collab/artifacts/qo5gj71j_de23a5_ef0c05dc2afd4b4d87563448ca96f4be~mv2.jpg" 
                  alt="H&E Removals"
                  className="w-16 h-16 object-contain rounded-lg bg-white/10 p-1"
                />
                <div>
                  <h3 className="text-lg font-semibold">Need a Removal Company?</h3>
                  <p className="text-emerald-100 text-sm mt-0.5">
                    H&E Removals - Professional, reliable home moving services
                  </p>
                </div>
              </div>
              <Button 
                className="bg-white text-emerald-700 hover:bg-emerald-50 font-semibold px-6 shadow-lg"
                onClick={() => setQuoteModal(true)}
              >
                Get Instant Quote
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Cases</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <Building className="w-8 h-8 text-slate-300" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.active}</p>
                </div>
                <Clock className="w-8 h-8 text-blue-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Needs Action</p>
                  <p className="text-2xl font-bold text-amber-600">{stats.needsAction}</p>
                </div>
                <AlertCircle className="w-8 h-8 text-amber-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Completed</p>
                  <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Cases Tabs */}
        <Tabs defaultValue="active">
          <TabsList>
            <TabsTrigger value="active">
              Active ({stats.active})
            </TabsTrigger>
            <TabsTrigger value="needs_action">
              Needs Action ({stats.needsAction})
            </TabsTrigger>
            <TabsTrigger value="all">
              All Cases ({stats.total})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="active" className="mt-4">
            <CasesList 
              cases={cases.filter(c => c.status === "active")} 
              navigate={navigate}
              getStageProgress={getStageProgress}
              formatDate={formatDate}
              userRole={user.role}
            />
          </TabsContent>

          <TabsContent value="needs_action" className="mt-4">
            <CasesList 
              cases={cases.filter(c => c.pending_milestones?.length > 0)} 
              navigate={navigate}
              getStageProgress={getStageProgress}
              formatDate={formatDate}
              userRole={user.role}
              showPending={true}
            />
          </TabsContent>

          <TabsContent value="all" className="mt-4">
            <CasesList 
              cases={cases} 
              navigate={navigate}
              getStageProgress={getStageProgress}
              formatDate={formatDate}
              userRole={user.role}
            />
          </TabsContent>
        </Tabs>

        {/* Empty State */}
        {cases.length === 0 && (
          <Card className="mt-6 border-dashed">
            <CardContent className="py-12 text-center">
              <Building className="w-16 h-16 text-slate-200 mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No Cases Yet</h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                When Goodacres adds you to a sale, it will appear here. You&apos;ll be able to 
                track progress and update milestones you&apos;re responsible for.
              </p>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Removal Quote Modal */}
      <RemovalsQuoteModal
        open={quoteModal}
        onOpenChange={setQuoteModal}
        user={user}
        cases={cases}
        showLinkedProperty={true}
      />
    </div>
  );
}

// Cases List Component
function CasesList({ cases, navigate, getStageProgress, formatDate, userRole, showPending = false }) {
  if (cases.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-8 text-center">
          <p className="text-muted-foreground">No cases in this category</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {cases.map((caseItem) => (
        <Card 
          key={caseItem.case_id} 
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate(`/sales/case/${caseItem.case_id}`)}
          data-testid={`case-card-${caseItem.case_id}`}
        >
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-4">
              {/* Left: Property Info */}
              <div className="flex items-start gap-4 flex-1">
                <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                  <Home className="w-6 h-6 text-slate-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold truncate">{caseItem.address_line1}</h3>
                  <p className="text-sm text-muted-foreground">{caseItem.postcode}</p>
                  
                  <div className="flex items-center gap-4 mt-2">
                    <Badge variant="outline" className="text-xs">
                      {caseItem.acting_for === "buyer" ? "Acting for Buyer" : "Acting for Seller"}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      £{caseItem.offer_price?.toLocaleString()}
                    </span>
                  </div>

                  {/* Show pending milestones if requested */}
                  {showPending && caseItem.pending_milestones?.length > 0 && (
                    <div className="mt-3 p-2 bg-amber-50 rounded-lg border border-amber-200">
                      <p className="text-xs font-medium text-amber-700 mb-1">Your pending updates:</p>
                      <div className="flex flex-wrap gap-1">
                        {caseItem.pending_milestones.map((m, idx) => (
                          <Badge key={idx} className="bg-amber-100 text-amber-800 text-xs">
                            {m.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Right: Stage & Progress */}
              <div className="text-right shrink-0">
                <Badge className={
                  caseItem.status === "completed" ? "bg-green-100 text-green-700" :
                  caseItem.status === "exchanged" ? "bg-purple-100 text-purple-700" :
                  "bg-blue-100 text-blue-700"
                }>
                  {stageLabels[caseItem.current_stage] || caseItem.current_stage}
                </Badge>
                
                {/* Progress bar */}
                <div className="mt-2 w-32">
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                    <span>Progress</span>
                    <span>{getStageProgress(caseItem.current_stage)}%</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-1.5">
                    <div 
                      className="bg-emerald-500 h-1.5 rounded-full transition-all"
                      style={{ width: `${getStageProgress(caseItem.current_stage)}%` }}
                    />
                  </div>
                </div>

                {/* ETA if set */}
                {caseItem.next_eta && (
                  <p className="text-xs text-muted-foreground mt-2 flex items-center justify-end gap-1">
                    <Calendar className="w-3 h-3" />
                    ETA: {formatDate(caseItem.next_eta)}
                  </p>
                )}
              </div>

              <ChevronRight className="w-5 h-5 text-slate-300" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
